'use client'

import { tw } from 'utils/classnames'
import * as React from 'react'
import { Tooltip } from 'primitives/tooltip'
import { useTranslations } from 'next-intl'
import { QuestionCircleIcon } from 'primitives/icons/general'
import { CheckCircleIcon, XCloseIcon } from 'primitives/icons'
import { useWindowWidth } from 'hooks/use-window-width'
import { useOnClickOutside } from 'hooks/use-on-click-outside'
import { useDomEvent } from 'hooks/use-dom-event'
import { useUserQuery } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'

import { LFS_URL } from '@web/utils/env'
import { TextWithAnimatedProductCount } from '@web/components/text-with-animated-numbers/text-with-anmated-product-count'

const ANIMATION_STEP = 1000

export function ReviewsTab() {
  const { data: userData } = useUserQuery()
  const isUserLoggedIn = isLoggedInQuerySelector(userData)

  const isEligibleForAllAccessFreeTrial =
    userData?.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial

  const hideFreeTrial = isUserLoggedIn && !isEligibleForAllAccessFreeTrial

  const t = useTranslations('pages.subscription')

  return (
    <div
      className={tw(
        hideFreeTrial
          ? 'grid-cols-1 lg:grid-cols-[1fr,minmax(0,_356px)]'
          : 'grid-cols-2 lg:grid-cols-[1fr,minmax(0,_356px),minmax(0,_344px)]',
        'grid font-bold',
      )}
    >
      <Title>{t('compare.creative_assets.title')}</Title>
      <Row hideFreeTrialColumn={hideFreeTrial}>
        <QuestionMark tooltip={t('compare.creative_assets.items.1.tooltip')}>
          {t('compare.creative_assets.items.1.copy')}
        </QuestionMark>
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <TextWithAnimatedProductCount
          className="mr-1"
          translationKey="pages.subscription.compare.creative_assets.items.2.copy"
          productType="graphics"
          step={ANIMATION_STEP}
        />
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <TextWithAnimatedProductCount
          className="mr-1"
          translationKey="pages.subscription.compare.creative_assets.items.3.copy"
          productType="fonts"
          step={ANIMATION_STEP}
        />
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <QuestionMark tooltip={t('compare.creative_assets.items.4.tooltip')}>
          <TextWithAnimatedProductCount
            className="mr-1"
            translationKey="pages.subscription.compare.creative_assets.items.4.copy"
            productType="crafts"
            step={ANIMATION_STEP}
          />
        </QuestionMark>
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <TextWithAnimatedProductCount
          className="mr-1"
          translationKey="pages.subscription.compare.creative_assets.items.5.copy"
          productType="embroidery"
          step={ANIMATION_STEP}
        />
      </Row>

      <Title>{t('compare.licensing.title')}</Title>
      <Row hideFreeTrialColumn={hideFreeTrial}>
        <QuestionMark tooltip={t('compare.licensing.items.1.tooltip')}>
          {t('compare.licensing.items.1.copy')}
        </QuestionMark>
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <QuestionMark tooltip={t('compare.licensing.items.2.tooltip')}>
          {t('compare.licensing.items.2.copy')}
        </QuestionMark>
      </Row>

      <Row hideFreeTrialColumn={hideFreeTrial}>
        <QuestionMark tooltip={t('compare.licensing.items.3.tooltip')}>
          {t('compare.licensing.items.3.copy')}
        </QuestionMark>
      </Row>

      <Title>{t('compare.learning.title')}</Title>
      <Row hideFreeTrialColumn={hideFreeTrial}>{t('compare.learning.items.1.copy')}</Row>

      <Heading>{t('compare.learning.items.2.copy')}</Heading>
      {hideFreeTrial ? null : <Item odd icon="close" />}
      <Item />
    </div>
  )
}

function Row({
  children,
  hideFreeTrialColumn,
}: {
  children: React.ReactNode
  hideFreeTrialColumn: boolean
}) {
  return (
    <>
      <Heading>{children}</Heading>
      {hideFreeTrialColumn ? null : <Item odd />}
      <Item />
    </>
  )
}

function Title({
  className,
  children,
  ...rest
}: React.HTMLProps<HTMLParagraphElement> & { children: string }) {
  const titleCanSplit = children.includes('-')
  const title = titleCanSplit ? children.split('-') : children

  return (
    <p
      className={tw(
        'border-primary-700 col-span-full mt-5 border-b-2 py-[6px] text-[15px] leading-[22px] first:mt-0',
        className,
      )}
      {...rest}
    >
      {titleCanSplit ? (
        <>
          {title[0]}
          <span
            className="bg-contain bg-right bg-no-repeat py-[6px] pr-[12px]"
            style={{
              backgroundImage: `url(${LFS_URL}/web/pages/subscription/Brush-Swipe_Subscription.svg)`,
            }}
          >
            - {title[1]}
          </span>
        </>
      ) : (
        title
      )}
    </p>
  )
}

function Text({ className, hidden, ...rest }: React.HTMLProps<HTMLParagraphElement>) {
  return (
    <div
      className={tw(
        'border-b border-gray-300 py-[10px] pr-[10px] text-[15px] leading-[22px]',
        hidden && 'hidden',
        className,
      )}
      {...rest}
    />
  )
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <Text className="col-span-full flex items-center lg:col-span-1 lg:min-w-[360px]">
      {children}
    </Text>
  )
}

function Item({
  odd,
  icon = 'check',
  ...rest
}: React.HTMLProps<HTMLParagraphElement> & { odd?: boolean; icon?: 'check' | 'close' }) {
  return (
    <Text
      className={tw(
        'flex items-center justify-center p-[10px] font-normal',
        odd && 'border-x-primary-700 bg-gray-bg border-x-2',
      )}
      {...rest}
    >
      {icon === 'check' ? <Check /> : <XCloseIcon className="size-7 text-[#FF4554]" />}
    </Text>
  )
}

function Check() {
  return <CheckCircleIcon className="text-secondary-500 !h-auto w-[21px]" />
}

function QuestionMark({ children, tooltip }: { children: React.ReactNode; tooltip: string }) {
  const [open, setOpen] = React.useState(false)
  const windowWidth = useWindowWidth()

  const ref = React.useRef<HTMLDivElement>(null!)
  useOnClickOutside(ref, () => setOpen(false))

  const handleTooltipClose = () => {
    setOpen(false)
  }

  const handleTooltipOpen = () => {
    setOpen(true)
  }

  useDomEvent(() => window, 'scroll', handleTooltipClose, { passive: true })

  return (
    <div className="inline-flex items-center">
      {children}

      <Tooltip
        open={open}
        delay={0}
        value={tooltip}
        side="top"
        className="text-12 bg-secondary-500 max-w-[324px] p-4 shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.25),_0px_8px_12px_6px_rgba(0,_0,_0,_0.15),_0px_0px_40px_0px_rgba(0,_0,_0,_0.08)]"
        arrowClassName="fill-secondary-500"
      >
        <div ref={ref}>
          <QuestionCircleIcon
            onMouseEnter={() => {
              if (windowWidth && windowWidth < 768) {
                return
              }
              handleTooltipOpen()
            }}
            onMouseLeave={() => {
              if (windowWidth && windowWidth < 768) {
                return
              }
              handleTooltipClose()
            }}
            onClick={() => {
              setOpen(prev => !prev)
            }}
            className="ml-2 h-[auto] w-[16px] text-[#A0B2B8]"
          />
        </div>
      </Tooltip>
    </div>
  )
}
